import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import Onboarding from './components/Onboarding';
import PricingPage from './components/PricingPage';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionManager from './components/User/SubscriptionManager';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import UserDashboard from './components/User/UserDashboard';
import HistoryStats from './components/User/HistoryStats';
import NotesJournal from './components/User/NotesJournal';
import Settings from './components/User/Settings';
import AccountSettings from './components/User/AccountSettings';
import ProgramsView from './components/User/ProgramsView';
import ChatPage from './components/Chatbot/ChatPage';
import ChatWidget from './components/Chatbot/ChatWidget';
import PurchaseCoins from './components/Credits/PurchaseCoins';
import CreditHistory from './components/Credits/CreditHistory';
import AdminDashboard from './components/Admin/AdminDashboard';
import PractitionerDashboard from './components/Practitioner/PractitionerDashboard';
import ClientDetailView from './components/Practitioner/ClientDetailView';
import PushNotificationPermission from './components/PushNotificationPermission';
import CheckoutPage from './components/Checkout/CheckoutPage';
import CheckoutSuccess from './components/Checkout/CheckoutSuccess';
import BankTransferPage from './components/Checkout/BankTransferPage';
import CheckoutError from './components/Checkout/CheckoutError';
import OnetimeProductPage from './components/Shop/OnetimeProductPage';
import AudioPlayerNew from './components/AudioPlayerNew';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ProgramProvider } from './context/ProgramContext';
import { AudioProvider } from './context/AudioContext';
import { Toaster } from './components/ui/sonner';
import { useReferralTracking } from './hooks/useReferralTracking';

function PrivateRoute({ children, adminOnly = false, practitionerOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Laden...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (practitionerOnly) {
    const roles = user.roles || [user.role];
    if (!roles.includes('practitioner') && user.role !== 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}

// Smart redirect component for root path
function RootRedirect() {
  const { user, loading } = useAuth();
  
  // Use useEffect to ensure redirect happens even if Navigate doesn't work
  useEffect(() => {
    // Double-check redirect after a short delay
    const timer = setTimeout(() => {
      if (window.location.pathname === '/' || window.location.pathname === '') {
        const token = localStorage.getItem('token');
        const targetPath = user || token ? '/dashboard' : '/login';
        window.location.replace(targetPath);
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [user, loading]);
  
  // Immediate redirect attempt
  if (!loading) {
    if (user) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  
  // Show loading state while checking auth
  return (
    <div className="loading-container">
      <div className="spinner"></div>
      <p>Laden...</p>
    </div>
  );
}

// Public route that redirects to dashboard if already logged in
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Laden...</p>
      </div>
    );
  }
  
  // If user is already logged in, redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function AppContent() {
  const { user } = useAuth();
  const location = window.location.pathname;
  
  // Track referral from URL ?ref= parameter
  useReferralTracking();
  
  // Show chat widget only on app pages, not on public pages
  const showChatWidget = user && ![
    '/', 
    '/login', 
    '/register', 
    '/onboarding', 
    '/pricing',
    '/subscription/success',
    '/subscription/cancel'
  ].includes(location);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/bank-transfer" element={<BankTransferPage />} />
        <Route path="/checkout/error" element={<CheckoutError />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<PricingPage />} />
        <Route path="/shop/product/:productId" element={<OnetimeProductPage />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<Onboarding />} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <UserDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/history"
          element={
            <PrivateRoute>
              <HistoryStats />
            </PrivateRoute>
          }
        />
        <Route
          path="/notes"
          element={
            <PrivateRoute>
              <NotesJournal />
            </PrivateRoute>
          }
        />
        <Route
          path="/account"
          element={
            <PrivateRoute>
              <AccountSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/coins"
          element={
            <PrivateRoute>
              <PurchaseCoins />
            </PrivateRoute>
          }
        />
        <Route
          path="/credit-history"
          element={
            <PrivateRoute>
              <CreditHistory />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <PrivateRoute>
              <SubscriptionManager />
            </PrivateRoute>
          }
        />
        <Route
          path="/subscription/manage"
          element={
            <PrivateRoute>
              <SubscriptionManager />
            </PrivateRoute>
          }
        />
        <Route
          path="/programs"
          element={
            <PrivateRoute>
              <ProgramsView />
            </PrivateRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <PrivateRoute>
              <ChatPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute adminOnly>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/practitioner"
          element={
            <PrivateRoute practitionerOnly>
              <PractitionerDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/practitioner/client/:clientId"
          element={
            <PrivateRoute practitionerOnly>
              <ClientDetailView />
            </PrivateRoute>
          }
        />
      </Routes>
      {showChatWidget && <ChatWidget />}
      <AudioPlayerNew />
      <PushNotificationPermission />
      <Toaster position="top-right" richColors />
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <ProgramProvider>
          <AudioProvider>
            <AppContent />
          </AudioProvider>
        </ProgramProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
